import { AxiosResponse } from 'axios';
import { NewPersonVM } from 'shell/components/shared/sipo-person-form/new-person';
import { HttpResponse } from 'shell/models/http-response';
import { ResponseAnprData, SubjectData } from 'shell/models/person';
import { transformGenerality } from 'shell/utils/consult-person/response-transformer/registry-office/generality';
import { DateTime } from 'luxon';
import { transformBirthdayDocument } from 'shell/utils/consult-person/response-transformer/registry-office/birthday-document';
import { transformToChoiceDate } from 'shell/components/shared/choice-date/functions';
import { transformCivilUnion } from 'shell/utils/consult-person/response-transformer/marital-status/civil-union';
import { transformMarriage } from 'shell/utils/consult-person/response-transformer/marital-status/marriage';
import { transformVoidCivilUnion } from 'shell/utils/consult-person/response-transformer/marital-status/void-civil-union';
import { transformVoidMarriage } from 'shell/utils/consult-person/response-transformer/marital-status/void-marriage';
import { transformWidowhood } from 'shell/utils/consult-person/response-transformer/marital-status/widowhood';
import { transformSubjectAttributes } from 'shell/utils/consult-person/response-transformer/registry-office/subject-attributes';
import { transformIdentityCard } from 'shell/utils/consult-person/response-transformer/documents/identity-card';
import { transformResidencyPermit } from 'shell/utils/consult-person/response-transformer/documents/residency-permit';
import { ResidenceOriginTransformer } from './transformer-person-to-register-office';
import { PersonalDataVM } from 'shell/view-models/personal-data';

export const transformerEditPerson = (
  response:
    | AxiosResponse<HttpResponse<SubjectData>>
    | AxiosResponse<HttpResponse<ResponseAnprData>>,
  municipalityIdentifier: string
): NewPersonVM => {
  const castedResponseSipo = response as AxiosResponse<HttpResponse<SubjectData>>;
  const castedResponseAnpr = response as AxiosResponse<HttpResponse<ResponseAnprData>>;

  const dataAnpr =
    (castedResponseAnpr.data?.payload?.risposta3002OK?.listaSoggetti?.datiSoggetto || [])[0] ||
    undefined;
  const data: SubjectData = dataAnpr || castedResponseSipo.data?.payload;

  const civilUnionResponse = transformCivilUnion(data);
  const marriageResponse = transformMarriage(data);
  const voidMarriageResponse = transformVoidMarriage(data);

  const generality = transformGenerality(data);

  return {
    isIrriperibile: data.datiCancellazione?.motivoCancellazione === '6',
    generality: generality
      ? {
          ...generality,
          municipalityIdentifier,
        }
      : undefined,
    birthdayDocument: transformBirthdayDocument(data) || undefined,
    citizenship:
      data?.cittadinanza && (data?.cittadinanza || [])?.length > 0
        ? [
            {
              code: data?.cittadinanza[0].codiceStato || '',
              citizenship: data?.cittadinanza[0].descrizioneStato || '',
              validityDate: data?.cittadinanza[0].dataValidita
                ? DateTime.fromISO(data?.cittadinanza[0].dataValidita).toFormat('dd/MM/yyyy')
                : '',
            },
          ]
        : undefined,
    paternity: data.paternita
      ? {
          generality: {
            fiscalCode: data.paternita?.generalita?.codiceFiscale?.codFiscale || '',
            surname: data.paternita?.generalita?.cognome || '',
            name: data.paternita?.generalita?.nome || '',
            withoutName: data.paternita?.generalita?.senzaNome === '1' ? true : false,
            withoutSurname: data.paternita?.generalita?.senzaCognome === '1' ? true : false,
            birthDate: data.paternita?.generalita?.dataNascita
              ? DateTime.fromISO(data.paternita?.generalita?.dataNascita)
              : null,
            birthChoiceDate: transformToChoiceDate(
              data.paternita?.generalita?.senzaGiorno || '',
              data.paternita?.generalita?.senzaGiornoMese || ''
            ),
            gender: data.paternita?.generalita?.sesso || '',
            isForeign: data.paternita?.generalita?.luogoNascita?.localita ? true : false,
            // generality italian form
            italian: data.paternita?.generalita?.luogoNascita?.comune
              ? {
                  location:
                    data.paternita?.generalita?.luogoNascita?.comune?.descrizioneLocalita || '',
                  province:
                    data.paternita?.generalita?.luogoNascita?.comune?.siglaProvinciaIstat || '',
                  municipality: data.paternita?.generalita?.luogoNascita?.comune?.codiceIstat
                    ? {
                        id: data.paternita?.generalita?.luogoNascita?.comune?.codiceIstat || '',
                        description:
                          data.paternita?.generalita?.luogoNascita?.comune?.nomeComune || '',
                        province:
                          data.paternita?.generalita?.luogoNascita?.comune?.siglaProvinciaIstat ||
                          '',
                      }
                    : null,
                }
              : undefined,
            // generality foreign form
            foreign: data.paternita?.generalita?.luogoNascita?.localita
              ? {
                  state: data.paternita?.generalita?.luogoNascita?.localita?.codiceStato
                    ? {
                        id: data.paternita?.generalita?.luogoNascita?.localita?.codiceStato || '',
                        value:
                          data.paternita?.generalita?.luogoNascita?.localita?.descrizioneStato ||
                          '',
                        province:
                          data.paternita?.generalita?.luogoNascita?.localita?.provinciaContea || '',
                      }
                    : null,
                  province:
                    data.paternita?.generalita?.luogoNascita?.localita?.provinciaContea || '',
                  municipality: data.paternita?.generalita?.luogoNascita?.localita?.codiceStato
                    ? {
                        id: data.paternita?.generalita?.luogoNascita?.localita?.codiceStato || '',
                        description:
                          data.paternita?.generalita?.luogoNascita?.localita?.descrizioneLocalita ||
                          '',
                        province:
                          data.paternita?.generalita?.luogoNascita?.localita?.provinciaContea || '',
                      }
                    : null,
                }
              : undefined,
            exceptionalBirthPlace: data.paternita?.generalita?.luogoNascita?.luogoEccezionale || '',
            municipalityIdentifier:
              data.paternita?.generalita?.idSchedaSoggettoComune?.idSchedaSoggetto || '',
            istatCode:
              data.paternita?.generalita?.idSchedaSoggettoComune?.idSchedaSoggettoComuneIstat || '',
            anprIdentifier: data.paternita?.generalita?.idSchedaSoggettoANPR || '',
            note: data.paternita?.generalita?.note || '',
            expatriationYear: data.paternita?.generalita?.annoEspatrio || '',
            aire: data.paternita?.generalita?.soggettoAIRE || 'N',
            validityFiscalCode:
              data.paternita?.generalita?.codiceFiscale?.descrizioneValiditaCF || '',
            validityFiscalCodeId: data.paternita?.generalita?.codiceFiscale?.validitaCF || '',
            validityDateFiscalCode: data.paternita?.generalita?.codiceFiscale
              ?.dataAttribuzioneValidita
              ? DateTime.fromISO(
                  data.paternita?.generalita?.codiceFiscale?.dataAttribuzioneValidita
                )
              : null,
          },
        }
      : undefined,
    maternity: data.maternita
      ? {
          generality: {
            fiscalCode: data.maternita?.generalita?.codiceFiscale?.codFiscale || '',
            surname: data.maternita?.generalita?.cognome || '',
            name: data.maternita?.generalita?.nome || '',
            withoutName: data.maternita?.generalita?.senzaNome === '1' ? true : false,
            withoutSurname: data.maternita?.generalita?.senzaCognome === '1' ? true : false,
            birthDate: data.maternita?.generalita?.dataNascita
              ? DateTime.fromISO(data.maternita?.generalita?.dataNascita)
              : null,
            birthChoiceDate: transformToChoiceDate(
              data.maternita?.generalita?.senzaGiorno || '',
              data.maternita?.generalita?.senzaGiornoMese || ''
            ),
            gender: data.maternita?.generalita?.sesso || '',
            isForeign: data.maternita?.generalita?.luogoNascita?.localita ? true : false,
            // generality italian form
            italian: data.maternita?.generalita?.luogoNascita?.comune
              ? {
                  location:
                    data.maternita?.generalita?.luogoNascita?.comune?.descrizioneLocalita || '',
                  province:
                    data.maternita?.generalita?.luogoNascita?.comune?.siglaProvinciaIstat || '',
                  municipality: data.maternita?.generalita?.luogoNascita?.comune?.codiceIstat
                    ? {
                        id: data.maternita?.generalita?.luogoNascita?.comune?.codiceIstat || '',
                        description:
                          data.maternita?.generalita?.luogoNascita?.comune?.nomeComune || '',
                        province:
                          data.maternita?.generalita?.luogoNascita?.comune?.siglaProvinciaIstat ||
                          '',
                      }
                    : null,
                }
              : undefined,
            // generality foreign form
            foreign: data.maternita?.generalita?.luogoNascita?.localita
              ? {
                  state: data.maternita?.generalita?.luogoNascita?.localita?.codiceStato
                    ? {
                        id: data.maternita?.generalita?.luogoNascita?.localita?.codiceStato || '',
                        value:
                          data.maternita?.generalita?.luogoNascita?.localita?.descrizioneStato ||
                          '',
                        province:
                          data.maternita?.generalita?.luogoNascita?.localita?.provinciaContea || '',
                      }
                    : null,
                  province:
                    data.maternita?.generalita?.luogoNascita?.localita?.provinciaContea || '',
                  municipality: data.maternita?.generalita?.luogoNascita?.localita?.codiceStato
                    ? {
                        id: data.maternita?.generalita?.luogoNascita?.localita?.codiceStato || '',
                        description:
                          data.maternita?.generalita?.luogoNascita?.localita?.descrizioneLocalita ||
                          '',
                        province:
                          data.maternita?.generalita?.luogoNascita?.localita?.provinciaContea || '',
                      }
                    : null,
                }
              : undefined,
            exceptionalBirthPlace: data.maternita?.generalita?.luogoNascita?.luogoEccezionale || '',
            municipalityIdentifier:
              data.maternita?.generalita?.idSchedaSoggettoComune?.idSchedaSoggetto || '',
            istatCode:
              data.maternita?.generalita?.idSchedaSoggettoComune?.idSchedaSoggettoComuneIstat || '',
            anprIdentifier: data.maternita?.generalita?.idSchedaSoggettoANPR || '',
            note: data.maternita?.generalita?.note || '',
            expatriationYear: data.maternita?.generalita?.annoEspatrio || '',
            aire: data.maternita?.generalita?.soggettoAIRE || 'N',
            validityFiscalCode:
              data.maternita?.generalita?.codiceFiscale?.descrizioneValiditaCF || '',
            validityFiscalCodeId: data.maternita?.generalita?.codiceFiscale?.validitaCF || '',
            validityDateFiscalCode: data.maternita?.generalita?.codiceFiscale
              ?.dataAttribuzioneValidita
              ? DateTime.fromISO(
                  data.maternita?.generalita?.codiceFiscale?.dataAttribuzioneValidita
                )
              : null,
          },
        }
      : undefined,
    civilStatus: data.statoCivile
      ? {
          note: data.statoCivile?.noteStatoCivile || undefined,
          status: data.statoCivile?.statoCivile || undefined,
        }
      : undefined,
    professionTitle: data?.altriDati
      ? {
          educationalQualification: data?.altriDati?.titoloStudio || '',
          professionalPosition: data?.altriDati?.posizioneProfessionale || '',
          unprofessionalCondition: data?.altriDati?.condizioneNonProfessionale || '',
          descrizioneCondizioneNonProfessionale:
            data?.altriDati?.descrizioneCondizioneNonProfessionale || '',
          descrizionePosizioneProfessionale:
            data?.altriDati?.descrizionePosizioneProfessionale || '',
          descrizioneTitoloStudio: data?.altriDati?.descrizioneTitoloStudio || '',
        }
      : undefined,
    civilUnion: civilUnionResponse
      ? {
          ...civilUnionResponse,
          generality: {
            ...civilUnionResponse?.generality,
            name: civilUnionResponse?.person?.name,
            surname: civilUnionResponse?.person?.surname,
            withoutName: civilUnionResponse?.person?.withoutName,
            withoutSurname: civilUnionResponse?.person?.withoutSurname,
            fiscalCode: civilUnionResponse?.person?.fiscalCode,
          },
        }
      : undefined,
    marriage: marriageResponse
      ? {
          ...marriageResponse,
          generality: {
            ...marriageResponse?.generality,
            name: marriageResponse?.person?.name,
            surname: marriageResponse?.person?.surname,
            withoutName: marriageResponse?.person?.withoutName,
            withoutSurname: marriageResponse?.person?.withoutSurname,
            fiscalCode: marriageResponse?.person?.fiscalCode,
          },
        }
      : undefined,
    voidCivilUnion: transformVoidCivilUnion(data) || undefined,
    voidMarriage: voidMarriageResponse || undefined,
    widowhood: transformWidowhood(data) || undefined,
    subjectAttributes: transformSubjectAttributes(data) || undefined,
    identityCard: transformIdentityCard(data) || undefined,
    residencyPermit: transformResidencyPermit(data) || undefined,
  };
};

export const transformerEditPersonFromIdIntestatario = (
  response: AxiosResponse<HttpResponse<SubjectData>> | AxiosResponse<HttpResponse<ResponseAnprData>>
): PersonalDataVM => {
  const castedResponseSipo = response as AxiosResponse<HttpResponse<SubjectData>>;
  const castedResponseAnpr = response as AxiosResponse<HttpResponse<ResponseAnprData>>;

  const dataAnpr =
    (castedResponseAnpr.data?.payload?.risposta3002OK?.listaSoggetti?.datiSoggetto || [])[0] ||
    undefined;

  const data: SubjectData = dataAnpr || castedResponseSipo.data?.payload;

  return {
    withSearch: true,
    generality: transformGenerality(data) || undefined,
    residenceOrigin: ResidenceOriginTransformer(data),
    datiCancellazione: data.datiCancellazione,
    subjectData: data,
    mother:
      data?.maternita?.generalita?.nome || data?.maternita?.generalita?.cognome
        ? {
            name: data?.maternita?.generalita?.nome,
            surname: data?.maternita?.generalita?.cognome,
          }
        : undefined,
    father:
      data?.paternita?.generalita?.nome || data?.paternita?.generalita?.cognome
        ? {
            name: data?.paternita?.generalita?.nome,
            surname: data?.paternita?.generalita?.cognome,
          }
        : undefined,
    civilUnion:
      data?.unioneCivile?.unitoCivilmente?.nome || data?.unioneCivile?.unitoCivilmente?.cognome
        ? {
            name: data?.unioneCivile?.unitoCivilmente?.nome,
            surname: data?.unioneCivile?.unitoCivilmente?.cognome,
          }
        : undefined,
    marriage:
      data?.matrimonio?.coniuge?.nome || data?.matrimonio?.coniuge?.cognome
        ? {
            name: data?.matrimonio?.coniuge?.nome,
            surname: data?.matrimonio?.coniuge?.cognome,
          }
        : undefined,
    identificationDocument: data.cartaIdentita
      ? {
          issuedDate: data.cartaIdentita?.dataRilascio
            ? DateTime.fromISO(data.cartaIdentita?.dataRilascio)
            : undefined,
          documentNumber: data.cartaIdentita?.numero || '',
          municipality: data.cartaIdentita?.comuneRilascio
            ? {
                provinceCode: data.cartaIdentita?.comuneRilascio.siglaProvinciaIstat || '',
                province: data.cartaIdentita?.comuneRilascio.siglaProvinciaIstat || '',
                id: data.cartaIdentita?.comuneRilascio.codiceIstat || '',
                description:
                  data.cartaIdentita?.comuneRilascio.denominazione ||
                  data.cartaIdentita?.comuneRilascio.nomeComune ||
                  '',
              }
            : undefined,
          isForeign: Boolean(data.cartaIdentita?.consolatoRilascio?.descrizioneConsolato),
          placeForeign: data.cartaIdentita?.consolatoRilascio?.descrizioneConsolato || '',
        }
      : undefined,
    citizenship:
      data?.cittadinanza && (data?.cittadinanza || [])?.length > 0
        ? [
            {
              code: data?.cittadinanza[0].codiceStato || '',
              citizenship: data?.cittadinanza[0].descrizioneStato || '',
              validityDate: data?.cittadinanza[0].dataValidita
                ? DateTime.fromISO(data?.cittadinanza[0].dataValidita).toFormat('dd/MM/yyyy')
                : '',
            },
          ]
        : undefined,
  };
};
